import React from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import theme from '../../theme';
import { useAuth } from '../../contexts/AuthContext';
import { useSuppliersImportStatus } from '../../selectors/selectors';
import { SupplierImportStatus, SuppliersImportStatus as SuppliersImportStatusData } from '../../types/SuppliersImportStatus';

const FRESH_COLOR = '#95cdb1';

// Green for an import that ran today, red for every other day and for a
// supplier that has never been imported
const isImportedToday = (updatedAt: string | null): boolean => {
  if (!updatedAt) {
    return false;
  }
  const imported = dayjs(updatedAt);

  return imported.isValid() && imported.isSame(dayjs(), 'day');
};

type RowProps = {
  status: SupplierImportStatus;
};

const ImportStatusRow: React.FC<RowProps> = ({ status }) => {
  const { t } = useTranslation(['main']);
  const importDate = status.updated_at
    ? `${t('main:importStatus.updatedOn')} ${dayjs(status.updated_at).format('DD.MM.YYYY')}`
    : t('main:importStatus.never');

  return (
    <Box
      display='flex'
      alignItems='flex-start'
      gap={theme.spacing(1)}
      p={theme.spacing(1.25, 0)}
      borderBottom={`1px solid ${theme.custom.borderGrey}`}
      sx={{
        '&:last-of-type': {
          borderBottom: 'none',
          paddingBottom: 0,
        },
      }}
    >
      <Box
        width='8px'
        height='8px'
        mt='6px'
        borderRadius='50%'
        flexShrink={0}
        bgcolor={isImportedToday(status.updated_at) ? FRESH_COLOR : theme.palette.primary.red}
      />
      <Box flexGrow={1} minWidth={0}>
        <Box display='flex' justifyContent='space-between' gap={theme.spacing(1)}>
          <Typography variant='body2' fontWeight='bold' color={theme.palette.primary.darkGrey}>
            {status.supplier}
          </Typography>
          <Typography variant='body2' color={theme.palette.primary.grey} whiteSpace='nowrap'>
            {`${status.count} ${t('main:importStatus.products')}`}
          </Typography>
        </Box>
        <Typography variant='body1' color={theme.palette.primary.grey}>
          {importDate}
        </Typography>
      </Box>
    </Box>
  );
};

type PanelProps = {
  statuses: SuppliersImportStatusData | undefined;
  isLoading: boolean;
  isError: boolean;
};

// Presentation only, so it can be rendered from anywhere the data is already available
export const ImportStatusPanel: React.FC<PanelProps> = ({ statuses, isLoading, isError }) => {
  const { t } = useTranslation(['main']);

  return (
    <Box
      p={theme.spacing(2)}
      bgcolor={theme.custom.whiteBackground}
      border={`1px solid ${theme.custom.borderGrey}`}
      borderRadius='4px'
    >
      <Typography variant='h6' mb={theme.spacing(1)}>
        {t('main:importStatus.title')}
      </Typography>
      {isLoading &&
        <Typography variant='body1' color={theme.palette.primary.grey}>
          {t('main:importStatus.loading')}
        </Typography>}
      {isError &&
        <Typography variant='body1' color={theme.palette.primary.red}>
          {t('main:importStatus.error')}
        </Typography>}
      {!!statuses && !statuses.length &&
        <Typography variant='body1' color={theme.palette.primary.grey}>
          {t('main:importStatus.empty')}
        </Typography>}
      {statuses?.map(status => (
        <ImportStatusRow key={status.supplier} status={status} />
      ))}
    </Box>
  );
};

export const SuppliersImportStatus: React.FC = () => {
  const { userLevel } = useAuth();
  const userIsLevel4 = userLevel === 'level_4';
  const { data, isLoading, isError } = useSuppliersImportStatus(userIsLevel4);

  if (!userIsLevel4) {
    return null;
  }

  return (
    <Box mt={theme.spacing(2)}>
      <ImportStatusPanel statuses={data} isLoading={isLoading} isError={isError} />
    </Box>
  );
};
